import type {
	AdminDiscount,
	CreateDiscountBody,
	CreateDiscountPositionBody,
	DiscountGrant,
	DiscountPosition,
	GrantDiscountBody,
	ListDiscountPositionsParams,
	ListDiscountsParams,
	UpdateDiscountBody,
	UpdateDiscountPositionBody,
} from "@/features/discounts/api/types";
import { toAdminSearchParams } from "@/shared/api/admin-list";
import { api } from "@/shared/api/client";
import type { InfinityPage } from "@/shared/api/types";

export async function listAdminDiscounts(
	params: ListDiscountsParams = {},
): Promise<InfinityPage<AdminDiscount>> {
	return api
		.get("admin/discounts", {
			searchParams: toAdminSearchParams({
				...params,
				sort: params.sort ?? "createdAt",
				order: params.order ?? "DESC",
			}),
		})
		.json();
}

export async function createAdminDiscount(
	body: CreateDiscountBody,
): Promise<AdminDiscount> {
	return api.post("admin/discounts", { json: body }).json();
}

export async function updateAdminDiscount(
	id: string,
	body: UpdateDiscountBody,
): Promise<AdminDiscount> {
	return api.patch(`admin/discounts/${id}`, { json: body }).json();
}

export async function deleteAdminDiscount(id: string): Promise<void> {
	await api.delete(`admin/discounts/${id}`);
}

export async function listDiscountGrants(
	discountId: string,
	params: { page?: number; limit?: 20 | 50 | 100 } = {},
): Promise<InfinityPage<DiscountGrant>> {
	return api
		.get(`admin/discounts/${discountId}/grants`, {
			searchParams: toAdminSearchParams(params),
		})
		.json();
}

export async function grantDiscount(
	discountId: string,
	body: GrantDiscountBody,
): Promise<DiscountGrant> {
	return api
		.post(`admin/discounts/${discountId}/grants`, { json: body })
		.json();
}

export async function revokeDiscountGrant(
	discountId: string,
	userId: string,
): Promise<void> {
	await api.delete(`admin/discounts/${discountId}/grants/${userId}`);
}

export async function listDiscountPositions(
	params: ListDiscountPositionsParams = {},
): Promise<InfinityPage<DiscountPosition>> {
	return api
		.get("admin/discount-positions", {
			searchParams: toAdminSearchParams({
				...params,
				sort: "sortOrder",
				order: "ASC",
			}),
		})
		.json();
}

export async function createDiscountPosition(
	body: CreateDiscountPositionBody,
): Promise<DiscountPosition> {
	return api.post("admin/discount-positions", { json: body }).json();
}

export async function updateDiscountPosition(
	id: string,
	body: UpdateDiscountPositionBody,
): Promise<DiscountPosition> {
	return api.patch(`admin/discount-positions/${id}`, { json: body }).json();
}

export async function deleteDiscountPosition(id: string): Promise<void> {
	await api.delete(`admin/discount-positions/${id}`);
}

export const discountKeys = {
	all: ["admin-discounts"] as const,
	list: (params: ListDiscountsParams) =>
		["admin-discounts", "list", params] as const,
	grants: (discountId: string, page: number) =>
		["admin-discounts", "grants", discountId, page] as const,
	positions: (params: ListDiscountPositionsParams) =>
		["admin-discounts", "positions", params] as const,
};
